import {
  Dialog,
  DialogContent,
  Button,
  DialogTitle,
  Box,
  Stack,
  MenuItem,
  TextField,
} from '@mui/material';
import { useEffect, useState } from 'react';
import CancelButton from 'src/careflow/common/CancelButton';
import uuidv4 from 'src/utils/uuidv4';
import agent from 'src/api/agent';
import { isArray } from 'lodash';
import { ArrowBackIos } from '@mui/icons-material';
import * as Yup from 'yup';
import DispositionCard from '../ui/DispositionCard';
import CloseIconButton from 'src/components/CloseIconButton';

const DispositionEndDialog = ({
  isOpen = false,
  handlerAction,
  dataHandler,
  handlerIsDialog,
  i18n,
}: any) => {
  const {
    title = null,
    message = null,
    enableDisposition = false,
    disposition = null,
    transitions = null,
    workflowInstanceData = null,
  } = dataHandler ?? {};

  const { messageDisposition = null, dispositionCatalogCode = 'DISPOSITION_REASONS' } =
    disposition ?? {};

  const [openDialog, setOpenDialog] = useState(isOpen);
  const [transitionSelected, setTransitionSelected] = useState<any>(null);
  const [reasonItems, setReasonItems] = useState<any>([]);
  const [reasonCode, setReasonCode] = useState<string | null>(null);
  const [reasonNote, setReasonNote] = useState<string | null>(null);
  const [errors, setErrors] = useState<any>(null);

  const fetchCatalogReason = async (catalogCode: string) => {
    const catalog = await agent.Catalog.getCatalogByCode(catalogCode);
    setReasonItems(catalog);
  };

  const clear = () => {
    setTransitionSelected(null);
    setErrors(null);
    setReasonCode(null);
    setReasonNote(null);
  };

  const handleCloseForm = () => {
    setOpenDialog(false);
    handlerIsDialog(false);
    clear();
  };

  const EventSchema = Yup.object().shape({
    reasonCode: Yup.string().max(255).required('Reason is required'),
    reasonNote: Yup.string()
      .nullable()
      .when([], {
        is: () => dataHandler?.dispositionReasonNoteIsRequired,
        then: (schema) => schema.required('Reason Note is required'),
        otherwise: (schema) => schema.notRequired(),
      }),
  });

  const validateFields = async () => {
    const validateFieldsSchema = await EventSchema.validate(
      {
        reasonCode: reasonCode?.trim(),
        reasonNote: reasonNote,
      },
      { abortEarly: false }
    ).catch((err) => err);

    if (validateFieldsSchema.inner) {
      const errors = validateFieldsSchema.inner.reduce((acc, error) => {
        acc[error.path] = error.message;
        return acc;
      }, {});

      setErrors((prev) => ({
        ...prev,
        reasonCode: errors.reasonCode || false,
        reasonNote: errors.reasonNote || false,
      }));

      return false;
    }

    setErrors(() => ({
      reasonCode: false,
      reasonNote: false,
    }));

    return true;
  };

  const handlerDisposition = async () => {
    const validate = await validateFields();

    if (!validate) return;

    const { status = null, subStatus = null } =
      JSON.parse(transitionSelected?.metadata)?.ui?.action ?? {};

    const dataAction = {
      status: status,
      subStatus: subStatus,
      reason: reasonCode,
      reasonNote: reasonNote,
      data: workflowInstanceData,
      transitionId: transitionSelected?.id,
    };

    handlerAction(dataAction, 'last');
    handleCloseForm();
  };

  useEffect(() => {
    setOpenDialog(isOpen);

    if (!enableDisposition) return;

    fetchCatalogReason(dispositionCatalogCode);
  }, [isOpen, dataHandler]);

  const showDisposition = Boolean(enableDisposition && transitionSelected !== null);

  return (
    <Dialog
      open={openDialog}
      onClose={handleCloseForm}
      aria-labelledby="confirm-dialog-title"
      aria-describedby="confirm-dialog-description"
      fullWidth
      maxWidth="sm"
    >
      <Box display="flex" alignItems="center" justifyContent="space-between">
        <DialogTitle sx={{ mb: 2, flex: 1 }}>{title}</DialogTitle>
        <Box sx={{ pr: 2 }}>
          <CloseIconButton
            onClick={handleCloseForm}
            data-name="disposition-end-dialog-close-button"
          />
        </Box>
      </Box>
      <DialogContent>{showDisposition ? messageDisposition : message}</DialogContent>

      {showDisposition ? (
        <Stack sx={{ mx: 4 }} spacing={2}>
          <TextField
            select
            value={reasonCode}
            id="reason-simple-select"
            label="Reason*"
            SelectProps={{
              displayEmpty: true,
            }}
            error={!!errors?.reasonCode}
            helperText={errors?.reasonCode}
            onChange={(event) => {
              if (!event.target.value) return;
              setReasonCode(String(event.target.value));
            }}
          >
            {reasonItems &&
              isArray(reasonItems) &&
              reasonItems?.map((option: any) => (
                <MenuItem key={option.uuid} value={option.valueDisplayName}>
                  {option?.longDisplayName}
                </MenuItem>
              ))}
          </TextField>
          <TextField
            value={reasonNote}
            name="reasonNote"
            label={`Reason Note ${dataHandler?.dispositionReasonNoteIsRequired ? '*' : ''}`}
            multiline
            rows={4}
            onChange={(event) => {
              if (!event.target.value) return;
              setReasonNote(String(event.target.value));
            }}
            error={!!errors?.reasonNote}
            helperText={errors?.reasonNote}
          />
        </Stack>
      ) : (
        <Box
          display="grid"
          gridTemplateColumns="repeat(auto-fit, minmax(200px, 1fr))"
          sx={{ mx: 8, py: 2 }}
          gap={2}
        >
          {transitions?.map((item) => {
            const { ui } = JSON.parse(item?.metadata);
            const dataAction = ui?.action?.dataAction ?? {};
            const data = {
              code: item?.code,
              ...dataAction,
              ...(dataAction?.title === 'Cancel' && { title: 'Close' }),
              errorMessage: ui?.action?.errorMessage,
              isValid: item?.isValid,
            };

            return (
              <DispositionCard
                key={uuidv4()}
                data={data}
                selected={transitionSelected?.code}
                handler={() => setTransitionSelected(item)}
                i18n={i18n}
              />
            );
          })}
        </Box>
      )}
      <Box display="grid" gridTemplateColumns="6fr 6fr" sx={{ mx: 2, py: 2 }}>
        <Button
          size="small"
          sx={{
            justifySelf: 'flex-start',
            ...(!showDisposition && {
              opacity: 0,
            }),
          }}
          onClick={clear}
          autoFocus={false}
        >
          <ArrowBackIos /> Back
        </Button>
        <Box justifySelf="flex-end">
          <CancelButton onClick={handleCloseForm}>Close</CancelButton>
          <Button disabled={!transitionSelected} onClick={handlerDisposition} variant="contained">
            {i18n('case.details.workflow.proceed', 'crs')}
          </Button>
        </Box>
      </Box>
    </Dialog>
  );
};

export default DispositionEndDialog;
